/**
 * Rubrique Ventes (plan validé du 17/10/2026) : Devis · Factures · Contrats,
 * chacun à son adresse (liens, notifications, e-mails). Une seule liste nourrit
 * l'entrée de la rubrique (`areas.ts`) et ses onglets (`features/sales`).
 */
import type { Capability } from '@myequip/shared';

export const SALES_PATHS = { devis: '/devis', factures: '/factures', contrats: '/contrats' } as const;

export type SalesSection = keyof typeof SALES_PATHS;

export interface SalesSectionEntry {
  key: SalesSection;
  to: string;
  capability: Capability;
  /** Adresses rangées sous cet onglet en plus de la sienne. */
  also: readonly string[];
}

const SECTIONS: readonly SalesSectionEntry[] = [
  { key: 'devis', to: SALES_PATHS.devis, capability: 'quote.manage', also: [] },
  { key: 'factures', to: SALES_PATHS.factures, capability: 'invoice.view', also: [] },
  // Les propositions de maintenance mènent à un contrat ou à un devis : rangées sous Contrats.
  { key: 'contrats', to: SALES_PATHS.contrats, capability: 'contract.view', also: ['/propositions-maintenance'] },
];

/** Les onglets que ce compte peut ouvrir, dans l'ordre. */
export function salesSections(can: (capability: Capability) => boolean): SalesSectionEntry[] {
  return SECTIONS.filter((section) => can(section.capability));
}

/** Adresse d'entrée de la rubrique : le premier onglet permis (les devis sinon). */
export function salesEntry(can: (capability: Capability) => boolean): string {
  return salesSections(can)[0]?.to ?? SALES_PATHS.devis;
}

/** L'onglet d'une adresse, ou `undefined` hors de la rubrique. */
export function salesSectionOf(pathname: string): SalesSection | undefined {
  const inside = (prefix: string): boolean => pathname === prefix || pathname.startsWith(`${prefix}/`);
  return SECTIONS.find((section) => [section.to, ...section.also].some(inside))?.key;
}
